import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  useTheme,
  alpha
} from '@mui/material';
import {
  Save as SaveIcon,
  Business as BusinessIcon,
  Payment as PaymentIcon,
  Category as CategoryIcon,
  Share as ShareIcon
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';

// Import custom components
import { MagicLoader } from '../../../design-system';
import { FormPageHeader } from '../../../components/ui/FormFields';
import { FormStepperEnhanced, FormStep } from '../../../components/shared/forms/stepper';

// Import vendor form sections
import VendorBasicInfoForm from './forms/VendorBasicInfoForm';
import VendorPaymentForm from './forms/VendorPaymentForm';
import VendorCategoriesForm from './forms/VendorCategoriesForm';
import VendorSocialMediaForm from './forms/VendorSocialMediaForm';

// Import hooks and stores
import useVendorStore from '../../../stores/vendorStore';
import useUIStore from '../../../stores/uiStore';

// Initial vendor data
const initialVendorData = {
  // Basic Information
  name: '',
  type: 'supplier',
  status: 'active',
  description: '',
  notes: '',
  taxId: '',
  contact: {
    email: '',
    phone: '',
    website: '',
    address: {
      street: '',
      city: '',
      state: '',
      postalCode: '',
      country: ''
    }
  },
  
  // Payment Information
  currency: 'USD',
  paymentInfo: {
    preferredMethod: 'bank_transfer',
    paymentTermsDays: 30,
    bankDetails: {
      bankName: '',
      accountName: '',
      accountNumber: '',
      iban: '',
      swiftCode: '',
      branchCode: ''
    },
    digitalPayment: {
      instapayNumber: '',
      paypalEmail: '',
      cryptoWallet: '',
      otherDetails: ''
    }
  },
  
  // Categories & Tags
  categories: [],
  specializations: [],
  tags: [],
  
  // Social Media (for influencers)
  socialMedia: {
    instagram: { handle: '', followers: '', engagementRate: '' },
    tiktok: { handle: '', followers: '', engagementRate: '' },
    youtube: { handle: '', subscribers: '', avgViews: '' },
    twitter: { handle: '', followers: '' },
    facebook: { handle: '', followers: '' },
    linkedin: { handle: '', connections: '' }
  }
};

// Validation schema
const getValidationSchema = (activeStep, vendorType) => {
  // Basic information validation (Step 0)
  const basicInfoSchema = Yup.object({
    name: Yup.string().required('Vendor name is required'),
    type: Yup.string().required('Vendor type is required'),
    status: Yup.string().required('Status is required'),
    contact: Yup.object({
      email: Yup.string().email('Invalid email format').nullable(),
      phone: Yup.string()
        .matches(/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/, 'Invalid phone format')
        .nullable(),
      website: Yup.string()
        .matches(/^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/, 'Invalid website URL')
        .nullable()
    })
  });
  
  // Payment validation (Step 1)
  const paymentSchema = Yup.object({
    paymentInfo: Yup.object({
      preferredMethod: Yup.string().required('Payment method is required'),
      paymentTermsDays: Yup.number()
        .min(0, 'Payment terms must be positive')
        .max(365, 'Payment terms cannot exceed 365 days')
    })
  });
  
  // Categories validation (Step 2)
  const categoriesSchema = Yup.object({
    categories: Yup.array().min(1, 'At least one category is required')
  });
  
  // Social media validation (Step 3 - only for influencers)
  const socialMediaSchema = Yup.object({
    socialMedia: Yup.object().test(
      'at-least-one-platform',
      'At least one social media platform is required',
      (socialMedia) => {
        return Object.values(socialMedia).some(platform => platform.handle);
      }
    )
  });
  
  // Switch based on active step
  switch (activeStep) {
    case 0:
      return basicInfoSchema;
    case 1:
      return paymentSchema;
    case 2:
      return categoriesSchema;
    case 3:
      return vendorType === 'influencer' ? socialMediaSchema : Yup.object({});
    default:
      return Yup.object({});
  }
};

const VendorForm = () => {
  const { id } = useParams();
  const theme = useTheme();
  const navigate = useNavigate();
  const isEditMode = !!id;
  
  // Store hooks
  const {
    currentVendor,
    isLoading,
    fetchVendorById,
    createVendor,
    updateVendor,
    checkVendorName
  } = useVendorStore();
  const { addNotification } = useUIStore();
  
  // State
  const [nameExists, setNameExists] = useState(false);
  const [isCheckingName, setIsCheckingName] = useState(false);
  
  // Form steps configuration - just define the structure, we'll add components in render
  const getStepConfig = (vendorType) => {
    const steps = [
      {
        label: 'Basic Information',
        description: 'General vendor details',
        icon: <BusinessIcon />
      },
      {
        label: 'Payment Details',
        description: 'Payment methods and terms',
        icon: <PaymentIcon />
      },
      {
        label: 'Categories',
        description: 'Services and specializations',
        icon: <CategoryIcon />
      }
    ];
    
    if (vendorType === 'influencer') {
      steps.push({
        label: 'Social Media',
        description: 'Social media presence',
        icon: <ShareIcon />
      });
    }
    
    return steps;
  };
  
  // Initialize formik
  const formik = useFormik({
    initialValues: initialVendorData,
    validationSchema: Yup.object({}),
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        let result;
        if (isEditMode) {
          result = await updateVendor(id, values);
          addNotification({
            message: 'Vendor updated successfully',
            type: 'success'
          });
        } else {
          result = await createVendor(values);
          addNotification({
            message: 'Vendor created successfully',
            type: 'success'
          });
        }
        navigate(`/finances/vendors/${result._id}`);
      } catch (error) {
        addNotification({
          message: error.message || `Failed to ${isEditMode ? 'update' : 'create'} vendor`,
          type: 'error'
        });
      }
    }
  });
  
  // Load vendor data if editing
  useEffect(() => {
    if (isEditMode && id) {
      fetchVendorById(id);
    }
  }, [id, isEditMode, fetchVendorById]);
  
  // Update form values when vendor is loaded
  useEffect(() => {
    if (isEditMode && currentVendor) {
      console.log('[VendorForm] Updating formik values with vendor:', currentVendor);
      formik.setValues({
        name: currentVendor.name || '',
        type: currentVendor.type || 'supplier',
        status: currentVendor.status || 'active',
        description: currentVendor.description || '',
        notes: currentVendor.notes || '',
        taxId: currentVendor.taxId || '',
        contact: {
          email: currentVendor.contact?.email || '',
          phone: currentVendor.contact?.phone || '',
          website: currentVendor.contact?.website || '',
          address: {
            street: currentVendor.contact?.address?.street || '',
            city: currentVendor.contact?.address?.city || '',
            state: currentVendor.contact?.address?.state || '',
            postalCode: currentVendor.contact?.address?.postalCode || '',
            country: currentVendor.contact?.address?.country || ''
          }
        },
        currency: currentVendor.currency || 'USD',
        paymentInfo: {
          preferredMethod: currentVendor.paymentInfo?.preferredMethod || 'bank_transfer',
          paymentTermsDays: currentVendor.paymentInfo?.paymentTermsDays || 30,
          bankDetails: {
            bankName: currentVendor.paymentInfo?.bankDetails?.bankName || '',
            accountName: currentVendor.paymentInfo?.bankDetails?.accountName || '',
            accountNumber: currentVendor.paymentInfo?.bankDetails?.accountNumber || '',
            iban: currentVendor.paymentInfo?.bankDetails?.iban || '',
            swiftCode: currentVendor.paymentInfo?.bankDetails?.swiftCode || '',
            branchCode: currentVendor.paymentInfo?.bankDetails?.branchCode || ''
          },
          digitalPayment: {
            instapayNumber: currentVendor.paymentInfo?.digitalPayment?.instapayNumber || '',
            paypalEmail: currentVendor.paymentInfo?.digitalPayment?.paypalEmail || '',
            cryptoWallet: currentVendor.paymentInfo?.digitalPayment?.cryptoWallet || '',
            otherDetails: currentVendor.paymentInfo?.digitalPayment?.otherDetails || ''
          }
        },
        categories: currentVendor.categories || [],
        specializations: currentVendor.specializations || [],
        tags: currentVendor.tags || [],
        socialMedia: {
          instagram: {
            handle: currentVendor.socialMedia?.instagram?.handle || '',
            followers: currentVendor.socialMedia?.instagram?.followers || '',
            engagementRate: currentVendor.socialMedia?.instagram?.engagementRate || ''
          },
          tiktok: {
            handle: currentVendor.socialMedia?.tiktok?.handle || '',
            followers: currentVendor.socialMedia?.tiktok?.followers || '',
            engagementRate: currentVendor.socialMedia?.tiktok?.engagementRate || ''
          },
          youtube: {
            handle: currentVendor.socialMedia?.youtube?.handle || '',
            subscribers: currentVendor.socialMedia?.youtube?.subscribers || '',
            avgViews: currentVendor.socialMedia?.youtube?.avgViews || ''
          },
          twitter: {
            handle: currentVendor.socialMedia?.twitter?.handle || '',
            followers: currentVendor.socialMedia?.twitter?.followers || ''
          },
          facebook: {
            handle: currentVendor.socialMedia?.facebook?.handle || '',
            followers: currentVendor.socialMedia?.facebook?.followers || ''
          },
          linkedin: {
            handle: currentVendor.socialMedia?.linkedin?.handle || '',
            connections: currentVendor.socialMedia?.linkedin?.connections || ''
          }
        }
      });
    }
  }, [currentVendor, isEditMode]);
  
  // Check vendor name availability
  useEffect(() => {
    const checkName = async () => {
      if (formik.values.name && (!isEditMode || formik.values.name !== currentVendor?.name)) {
        setIsCheckingName(true);
        try {
          const result = await checkVendorName(formik.values.name);
          setNameExists(result.exists);
          if (result.exists) {
            formik.setFieldError('name', 'A vendor with this name already exists');
          }
        } catch (error) {
          console.error('Error checking vendor name:', error);
          setNameExists(false);
        } finally {
          setIsCheckingName(false);
        }
      } else {
        setNameExists(false);
      }
    };
    
    const timeoutId = setTimeout(checkName, 500); // Debounce
    return () => clearTimeout(timeoutId);
  }, [formik.values.name, isEditMode, currentVendor]);
  
  if (isLoading && isEditMode) {
    return <MagicLoader />;
  }
  
  console.log('[VendorForm] Rendering with formik values:', formik.values);
  console.log('[VendorForm] Current vendor:', currentVendor);
  
  // Create steps configuration with components
  const stepConfig = getStepConfig(formik.values.type);
  const steps = stepConfig.map((config, index) => ({
    ...config,
    component: (
      <>
        {index === 0 && (
          <VendorBasicInfoForm
            formik={formik}
            isCheckingName={isCheckingName}
            nameExists={nameExists}
          />
        )}
        {index === 1 && <VendorPaymentForm formik={formik} />}
        {index === 2 && <VendorCategoriesForm formik={formik} />}
        {index === 3 && formik.values.type === 'influencer' && <VendorSocialMediaForm formik={formik} />}
      </>
    )
  }));
  
  return (
    <Box sx={{ pb: 8 }}>
      <FormPageHeader
        title={isEditMode ? 'Edit Vendor' : 'Create New Vendor'}
        subtitle={isEditMode ? `Update ${currentVendor?.name || 'vendor'} details` : 'Add a new vendor to your system'}
        breadcrumbs={[
          { label: 'Finances', path: '/finances' },
          { label: 'Vendors', path: '/finances/vendors' },
          { label: isEditMode ? 'Edit' : 'Create' }
        ]}
        backPath="/finances/vendors"
      />
      
      <FormStepperEnhanced
        steps={steps}
        onSubmit={formik.handleSubmit}
        isSubmitting={formik.isSubmitting}
        errors={formik.errors}
        touched={formik.touched}
        validationSchema={(activeStep) => getValidationSchema(activeStep, formik.values.type)}
        submitButtonText={isEditMode ? 'Update Vendor' : 'Create Vendor'}
        submitButtonIcon={<SaveIcon />}
      >
        {steps.map((step, index) => (
          <FormStep key={index}>{step.component}</FormStep>
        ))}
      </FormStepperEnhanced>
    </Box>
  );
};

export default VendorForm;